// @flow

import CookieManager from "@cabonline/cookies"
import apiConfig from "@cabonline/shared/config/apiConfig"
import { userCookie } from "@cabonline/shared/utils/cookies"
import Axios from "axios"
import { jwtDecode } from "jwt-decode"
import { action, runInAction } from "mobx"

import AuthStore from "../stores/AuthStore"
import IntlStore from "../stores/IntlStore"

let sharedConfig = {}

const configureEnvironmentBaseUrl = () => {
  if (typeof window === "undefined") return

  const { hostname } = window.location
  if (!hostname || hostname.indexOf("cabonline.com") === -1) return

  const lowerHostname = hostname.toLowerCase()
  const adminHostMatch = lowerHostname.match(
    /([a-z0-9-]*?)admin\.cabonline\.com$/,
  )
  const loginHostMatch = lowerHostname.match(
    /([a-z0-9-]*?)login\.cabonline\.com$/,
  )

  const envFromHost = adminHostMatch
    ? adminHostMatch[1].replace(/-+$/, "")
    : loginHostMatch
      ? loginHostMatch[1].replace(/-+$/, "")
      : ""

  // For production domains (admin.cabonline.com, login.cabonline.com),
  // envFromHost will be empty. In this case, always use production API
  // regardless of API_ENV build variable
  if (envFromHost === "" && (adminHostMatch || loginHostMatch)) {
    // Production domain detected, force production API URL
    const productionUrl = "https://api.cabonline.com"
    if (apiConfig.baseUrl) {
      const previousUrl = apiConfig.baseUrl.default
      if (previousUrl !== productionUrl) {
        console.log(
          `[MainApi] Production domain detected. Overriding API URL from ${previousUrl} to ${productionUrl}`,
        )
        apiConfig.baseUrl.default = productionUrl
      } else {
        console.log(
          `[MainApi] Production domain detected. API URL already set to ${productionUrl}`,
        )
      }
    }
    return
  }

  const envSlug = (apiConfig.environment || "").trim().toLowerCase()
  const resolvedEnv = envFromHost || envSlug

  if (!resolvedEnv || ["prod", "production", ""].includes(resolvedEnv)) {
    // Production should keep using https://api.cabonline.com
    return
  }
  const normalizedEnv = resolvedEnv.replace(/[^a-z0-9]+/gi, "-")
  const envOverrides = {
    test: "https://testapi.cabonline.com",
  }

  const isAdminDomain =
    adminHostMatch !== null ||
    lowerHostname.endsWith(`${normalizedEnv}-admin.cabonline.com`) ||
    lowerHostname.endsWith(`${normalizedEnv}admin.cabonline.com`)

  const isLoginDomain =
    loginHostMatch !== null ||
    lowerHostname.endsWith(`${normalizedEnv}-login.cabonline.com`) ||
    lowerHostname.endsWith(`${normalizedEnv}login.cabonline.com`)

  if (!isAdminDomain && !isLoginDomain) return

  const targetBaseUrl =
    envOverrides[normalizedEnv] || `https://${normalizedEnv}api.cabonline.com`

  if (apiConfig.baseUrl && apiConfig.baseUrl.default !== targetBaseUrl) {
    console.log(
      `[MainApi] ${normalizedEnv} environment detected. Setting API URL to ${targetBaseUrl}`,
    )
    apiConfig.baseUrl.default = targetBaseUrl
  }
}

configureEnvironmentBaseUrl()

class MainApi {
  tokenHasExpired = false
  isRefreshingToken = false
  expirationIntervalID: IntervalID
  authStore: AuthStore
  intlStore: IntlStore

  constructor(authStore: AuthStore, intlStore: IntlStore) {
    this.authStore = authStore
    this.intlStore = intlStore
  }

  _setAuthorization() {
    if (!this.authStore.auth) return

    sharedConfig = Object.assign({}, sharedConfig, {
      headers: {
        ...sharedConfig.headers,
        Authorization: `Bearer ${this.authStore.auth.accessToken}`,
      },
    })
  }

  _setExpirationTimeout(expiresIn: number) {
    this.tokenHasExpired = false

    if (this.expirationIntervalID) {
      clearInterval(this.expirationIntervalID)
    }

    const timeout = new Date().getTime() + expiresIn * 1000

    this.expirationIntervalID = setInterval(() => {
      if (new Date().getTime() > timeout) {
        this.tokenHasExpired = true
        clearInterval(this.expirationIntervalID)
      }
    }, 1000)
  }

  async _ensureFreshAccessToken() {
    if (!this.authStore.auth) return

    if (this.isRefreshingToken) {
      await new Promise((resolve) => {
        const poll = () => {
          if (this.isRefreshingToken) {
            return setTimeout(poll, 100)
          }
          return resolve()
        }

        setTimeout(poll, 100)
      })
    } else if (this.tokenHasExpired) {
      this.isRefreshingToken = true

      const loggedIn = await this.loginWithRefreshToken(
        this.authStore.auth.refreshToken,
        this.authStore.auth.email,
      )

      this.isRefreshingToken = false

      if (loggedIn) {
        await this.getAdministrator()
      }
    }
  }

  _buildConfig(config: ?Object) {
    const baseConfig = config ? { ...config } : {}
    const mergedHeaders = {
      ...(sharedConfig.headers || {}),
      ...(config && config.headers ? config.headers : {}),
    }

    const language = this.intlStore.currentLanguage
    if (language) {
      mergedHeaders["Accept-Language"] = language === "no" ? "nb" : language
    }

    const mergedConfig = {
      ...sharedConfig,
      ...baseConfig,
    }

    if (Object.keys(mergedHeaders).length > 0) {
      mergedConfig.headers = mergedHeaders
    }

    return mergedConfig
  }

  async _preRequest() {
    await this._ensureFreshAccessToken()
    this._setAuthorization()
  }

  async get(url: string, config: ?Object) {
    await this._preRequest()

    return Axios.get(
      `${apiConfig.baseUrl.default}/${url}`,
      this._buildConfig(config),
    )
  }

  async post(url: string, data: ?any, config: ?Object) {
    await this._preRequest()

    return Axios.post(
      `${apiConfig.baseUrl.default}/${url}`,
      data,
      this._buildConfig(config),
    )
  }

  async put(url: string, data: ?any, config: ?Object) {
    await this._preRequest()

    return Axios.put(
      `${apiConfig.baseUrl.default}/${url}`,
      data,
      this._buildConfig(config),
    )
  }

  async patch(url: string, data: ?any, config: ?Object) {
    await this._preRequest()

    return Axios.patch(
      `${apiConfig.baseUrl.default}/${url}`,
      data,
      this._buildConfig(config),
    )
  }

  async delete(url: string, data: ?any, config: ?Object) {
    await this._preRequest()

    return Axios.delete(
      `${apiConfig.baseUrl.default}/${url}`,
      this._buildConfig(config),
    )
  }

  @action
  async getAdministrator() {
    try {
      const adminResponse = await this.get("admin-api/api/administrators")
      const result = adminResponse.data.result

      if (result.length > 0) {
        runInAction(() => {
          if (this.authStore.auth) {
            this.authStore.auth.admin = result[0]
          }
        })
      }
    } catch (e) {
      return false
    }

    return true
  }

  @action
  async loginWithRefreshToken(refreshToken: string, email: string) {
    try {
      const response = await this.refreshAuth(refreshToken)
      const jwt = jwtDecode(response.data.access_token)

      this.authStore.setAuthFromResponse(jwt, response.data, email)
    } catch (e) {
      this.authStore.auth = null
      CookieManager.remove(userCookie.name)

      return false
    }

    return true
  }

  async login(email: string, password: string) {
    const response = await Axios.post(
      `${apiConfig.baseUrl.default}/security-login-api/api/system-user/auth/token`,
      {
        grant_type: "password",
        client_id: "admin",
        username: email,
        password: password,
      },
    )

    this._setExpirationTimeout(response.data.expires_in)

    return response
  }

  async refreshAuth(refreshToken: string) {
    const response = await Axios.post(
      `${apiConfig.baseUrl.default}/security-login-api/api/system-user/auth/token`,
      {
        grant_type: "refresh_token",
        client_id: "admin",
        code: refreshToken,
      },
    )

    this._setExpirationTimeout(response.data.expires_in)

    return response
  }

  cancableGetRequest() {
    let call

    return (url: string) => {
      if (call) {
        call.cancel()
      }

      call = Axios.CancelToken.source()

      return this.get(url, { cancelToken: call.token })
    }
  }
}

export default (new MainApi(AuthStore, IntlStore): any)
